import { useState } from 'react';

import { EmptyCard, LoadingBlock, PageHeader } from '@/components/PageHeader';
import { today } from '@/lib/format';

import { useSiteInspections, type ActionStatus, type SiteAction, type SiteInspection } from './siteSafeApi';

const BADGE: Record<ActionStatus, string> = { 지적: 'badge-red', 조치완료: 'badge-yellow', 확인완료: 'badge-green' };

/**
 * 현장 › 시정조치 — 협력사 포털의 점검 회차와 그 지적 · 회신 · 확인. 회차 등록 · 지적 · 확인은 포털(감리)에서 한다.
 * 위 칸은 아직 확인완료가 아닌 것만 보기 — 기본으로 켠다.
 */
export function SiteActionsPage({ siteId }: { siteId: string }) {
  const inspections = useSiteInspections(siteId);
  const [openOnly, setOpenOnly] = useState(true);
  const rounds = (inspections.data ?? [])
    .map((round) => ({ ...round, actions: openOnly ? round.actions.filter((one) => one.status !== '확인완료') : round.actions }))
    .filter((round) => !openOnly || round.actions.length > 0);

  return (
    <div className="space-y-5">
      <PageHeader
        title="시정조치"
        actions={
          <label className="flex cursor-pointer items-center gap-2 text-ui-sm">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            미완료만
          </label>
        }
      />
      {inspections.isPending ? (
        <LoadingBlock />
      ) : inspections.isError ? (
        <LoadingBlock />
      ) : rounds.length === 0 ? (
        <EmptyCard>해당하는 시정조치가 없습니다.</EmptyCard>
      ) : (
        rounds.map((round) => <Round key={round.id} round={round} />)
      )}
    </div>
  );
}

function Round({ round }: { round: SiteInspection }) {
  return (
    <div className="card p-0" style={{ overflowX: 'auto' }}>
      <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
        <b>{round.roundNo}회차</b>
        <span className="badge badge-gray">{round.inspectionKind}</span>
        <span className="text-ui-sm">{round.inspectedOn}</span>
        {round.inspector && (
          <span className="text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
            {round.inspector}
          </span>
        )}
      </div>
      {round.actions.length === 0 ? (
        <div className="px-4 py-3 text-ui-sm" style={{ color: 'var(--text-muted)' }}>
          지적 없음
        </div>
      ) : (
        <table className="site-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>지적 내용</th>
              <th style={{ width: '140px' }}>위치</th>
              <th style={{ width: '110px' }}>기한</th>
              <th style={{ width: '90px' }}>상태</th>
              <th style={{ textAlign: 'left' }}>조치 회신</th>
            </tr>
          </thead>
          <tbody>
            {round.actions.map((action) => (
              <ActionRow key={action.id} action={action} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ActionRow({ action }: { action: SiteAction }) {
  const late = action.status === '지적' && action.dueDate !== null && action.dueDate < today();
  return (
    <tr>
      <td style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>{action.finding}</td>
      <td>{action.location ?? '—'}</td>
      <td style={{ color: late ? 'var(--danger)' : undefined, fontWeight: late ? 700 : undefined }}>{action.dueDate ?? '—'}</td>
      <td>
        <span className={`badge ${BADGE[action.status] ?? 'badge-gray'}`}>{action.status}</span>
      </td>
      <td style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>
        {action.actionNote ?? '—'}
        {action.actedOn && <span style={{ color: 'var(--text-muted)' }}> · {action.actedOn}</span>}
      </td>
    </tr>
  );
}
